import { Configuration, LogLevel, RedirectRequest, SilentRequest } from '@azure/msal-browser';

/**
 * Microsoft Entra External ID configuration for the CaddieSight clubhouse SPA.
 * Golfers sign in with social accounts (Google, Facebook, Apple) or email through
 * the Entra External ID user flow. All values come from build-time env vars
 * (see `.env.local.example`):
 *  - REACT_APP_CLUBHOUSE_MSAL_CLIENT_ID    the SPA app registration client id
 *  - REACT_APP_CLUBHOUSE_MSAL_AUTHORITY    the External ID (CIAM) authority, e.g.
 *                                          https://<tenant>.ciamlogin.com/<tenant-id>
 *  - REACT_APP_CLUBHOUSE_MSAL_REDIRECT_URI where Entra returns after sign-in (defaults to origin)
 *
 * When no client id is configured, `msalConfigured` is false so the app fails
 * loudly in development instead of silently attempting an unconfigured sign-in.
 */
export const msalClientId = process.env.REACT_APP_CLUBHOUSE_MSAL_CLIENT_ID ?? '';

export const msalConfigured = msalClientId.trim().length > 0;

const authority =
  process.env.REACT_APP_CLUBHOUSE_MSAL_AUTHORITY ?? 'https://login.microsoftonline.com/common';

const redirectUri = process.env.REACT_APP_CLUBHOUSE_MSAL_REDIRECT_URI ?? window.location.origin;

/**
 * Entra External ID authorities live on a custom `ciamlogin.com` domain, so MSAL
 * cannot infer them from the well-known list. Register the host as "known" so
 * MSAL trusts it when discovering the OpenID metadata.
 */
function knownAuthorityHost(url: string): string[] {
  try {
    return [new URL(url).hostname];
  } catch {
    return [];
  }
}

export const msalConfig: Configuration = {
  auth: {
    clientId: msalClientId || 'unconfigured',
    authority,
    knownAuthorities: knownAuthorityHost(authority),
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Persist across tabs/PWA launches so golfers stay signed in between rounds.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        // eslint-disable-next-line no-console
        if (level === LogLevel.Error) console.error(message);
      },
    },
  },
};

/** Identity scopes requested from the External ID customer user flow. */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/** Forces the hosted email flow to prompt instead of reusing a social-provider SSO session. */
export const emailLoginRequest: RedirectRequest = {
  ...loginRequest,
  prompt: 'login',
};

/**
 * Identity scopes used to retrieve the cached External ID ID token for the
 * first-party CaddieSight session exchange.
 */
export const identityTokenRequest: SilentRequest = {
  scopes: loginRequest.scopes,
};
// Personal Microsoft accounts use the separate consumers client in microsoftAuth.ts.

